// /js/pages/register.js
// Register page controller (MPA — runs on page load, no initX wrapper).

import { supabase } from '/js/services/supabaseClient.js';
import { showToast } from '/js/modules/toast.js';

console.log('📌 register.js loaded');

// ============================================
// BOOT
// ============================================
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRegister);
} else {
    initRegister();
}

function initRegister() {
    console.log('📝 Register page initializing');

    const form = document.getElementById('registerForm');
    if (form) {
        form.addEventListener('submit', handleRegister);
    }
}

// ============================================
// SUBMIT HANDLER
// ============================================
async function handleRegister(e) {
    e.preventDefault();

    const name            = document.getElementById('registerName').value.trim();
    const email           = document.getElementById('registerEmail').value.trim();
    const password        = document.getElementById('registerPassword').value;
    const confirmPassword = document.getElementById('registerConfirmPassword').value;
    const agreeTerms      = document.getElementById('agreeTerms').checked;

    // ---- Client-side validation ----
    if (!name || !email || !password || !confirmPassword) {
        showToast('Please fill in all fields', 'warning');
        return;
    }
    if (password.length < 6) {
        showToast('Password must be at least 6 characters', 'warning');
        return;
    }
    if (password !== confirmPassword) {
        showToast('Passwords do not match', 'warning');
        return;
    }
    if (!agreeTerms) {
        showToast('Please agree to the Terms & Conditions', 'warning');
        return;
    }

    const btn        = document.getElementById('registerBtn');
    const btnText    = document.getElementById('registerBtnText');
    const btnSpinner = document.getElementById('registerBtnSpinner');

    btn.disabled = true;
    btnText.textContent = 'Creating Account...';
    btnSpinner.classList.remove('hidden');

    try {
        // ---- 1. Create auth user ----
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: { data: { display_name: name } }
        });

        if (error) {
            showToast(friendlySignupError(error), 'error');
            return;
        }

        // ---- 2. Create profile row in `users` table ----
        // Note: if you have a Postgres trigger (handle_new_user) that inserts
        // this row automatically, this insert will fail with a unique-constraint
        // error which we swallow. That's fine.
        if (data.user) {
            try {
                const { error: profileError } = await supabase
                    .from('users')
                    .insert([{
                        id: data.user.id,
                        email: email,
                        display_name: name,
                        role: 'customer',
                        is_active: true,
                        subscription: {
                            isActive: false,
                            plan: null,
                            startDate: null,
                            endDate: null,
                            autoRenew: false,
                        },
                        preferences: {
                            darkMode: false,
                            fontSize: 'medium',
                            notifications: true,
                            language: 'en',
                        },
                        stats: {
                            booksRead: 0,
                            totalReadingTime: 0,
                            booksPurchased: 0,
                            bookmarks: 0,
                        },
                        favorites: [],
                    }]);

                if (profileError) {
                    // "duplicate key" is OK (trigger already created it)
                    const msg = (profileError.message || '').toLowerCase();
                    if (msg.includes('duplicate') || msg.includes('unique')) {
                        console.log('ℹ️ User profile already exists (trigger handled it).');
                    } else {
                        console.warn('⚠️ Could not create user profile:', profileError);
                    }
                } else {
                    console.log('✅ User profile created in Supabase');
                }
            } catch (profileErr) {
                console.warn('⚠️ Profile insert threw:', profileErr);
            }
        }

        // ---- 3. Feedback ----
        if (data.user && !data.session) {
            showToast('Account created! Please check your email to verify.', 'success');
        } else {
            showToast('Account created successfully!', 'success');
        }

        // ---- 4. Redirect to login ----
        setTimeout(() => {
            window.location.href = '/login';
        }, 2500);

    } catch (err) {
        console.error('Registration error:', err);
        showToast('An error occurred: ' + err.message, 'error');
    } finally {
        btn.disabled = false;
        btnText.textContent = 'Create Account';
        btnSpinner.classList.add('hidden');
    }
}

// ============================================
// HELPERS
// ============================================
function friendlySignupError(error) {
    const msg = (error.message || '').toLowerCase();
    if (msg.includes('already registered') || msg.includes('already exists')) {
        return 'This email is already registered. Please login instead.';
    }
    if (msg.includes('password')) {
        return 'Password must be at least 6 characters.';
    }
    if (msg.includes('rate limit') || msg.includes('too many')) {
        return 'Too many attempts. Please wait a moment and try again.';
    }
    return error.message;
}